import type { Column } from '~shared/rundown/column'
/**
 * Converts a slot among the visible columns into an index in the full column
 * order, which also holds hidden columns.
 *
 * @param visibleIndex - Slot to insert before; `visibleColumns.length` or
 *   more means after the last visible column
 *
 * @returns Index in `layoutColumnIds` to insert before
 */
export function visibleToLayoutIndex(
  visibleColumns: Pick<Column, 'id'>[],
  layoutColumnIds: readonly Column['id'][],
  visibleIndex: number,
): number {
  if (visibleIndex >= visibleColumns.length) {
    const lastVisibleId = visibleColumns.at(-1)?.id
    return lastVisibleId
      ? layoutColumnIds.indexOf(lastVisibleId) + 1
      : layoutColumnIds.length
  }

  const targetId = visibleColumns[visibleIndex]?.id
  return targetId ? layoutColumnIds.indexOf(targetId) : visibleIndex
}

/**
 * The visible slot to insert before when moving a column one step left or
 * right.
 *
 * @remarks
 *   Right skips past the column's own slot, as inserting before the next column
 *   would leave it where it is.
 *
 * @returns Slot for `visibleToLayoutIndex`, or `null` when the column is not
 *   visible or already first
 */
export function getStepTargetSlot(
  visibleColumns: Pick<Column, 'id'>[],
  columnId: Column['id'],
  direction: 'left' | 'right',
): number | null {
  const visibleIndex = visibleColumns.findIndex(({ id }) => id === columnId)
  if (visibleIndex === -1) return null

  const target = direction === 'right' ? visibleIndex + 2 : visibleIndex - 1
  return target < 0 ? null : target
}

/**
 * Move a column id to insert before `moveTo` in the order.
 *
 * @param moveTo - Index in `order` to insert before, counted before the column
 *   is taken out; `order.length` means the end
 *
 * @returns The new order and the column's index in it, or `null` when there is
 *   nothing to write: the id is not in the order, `moveTo` is `-1` (eg. an id
 *   `visibleToLayoutIndex` could not find), or the column would stay in place
 */
export function moveColumnId(
  order: readonly Column['id'][],
  columnId: Column['id'],
  moveTo: number,
): { order: Column['id'][]; newIndex: number } | null {
  const oldIndex = order.indexOf(columnId)
  if (oldIndex === -1 || moveTo === -1) return null

  const newIndex = moveTo > oldIndex ? moveTo - 1 : moveTo
  if (newIndex === oldIndex) return null

  const next = [...order]
  next.splice(oldIndex, 1)
  next.splice(newIndex, 0, columnId)

  return { order: next, newIndex }
}

/**
 * Move a column to the drop slot `visibleSlot` among the visible columns.
 *
 * @remarks
 *   The slots right before and right after the column itself are a no-op. The
 *   layout check in `moveColumnId` alone misses the second one when a hidden
 *   column follows: "before the next visible column" lands past the hidden one,
 *   a move the user cannot see but that still rewrites the rundown's order.
 *
 * @returns Same as `moveColumnId`, or `null` when the visible order would not
 *   change
 */
export function moveColumnToVisibleSlot(
  visibleColumns: Pick<Column, 'id'>[],
  layoutColumnIds: readonly Column['id'][],
  columnId: Column['id'],
  visibleSlot: number,
): { order: Column['id'][]; newIndex: number } | null {
  const visibleIndex = visibleColumns.findIndex(({ id }) => id === columnId)
  if (visibleSlot === visibleIndex || visibleSlot === visibleIndex + 1) {
    return null
  }

  return moveColumnId(
    layoutColumnIds,
    columnId,
    visibleToLayoutIndex(visibleColumns, layoutColumnIds, visibleSlot),
  )
}
